
import { supabase } from '@/integrations/supabase/client';

export interface UserProfile {
  id: string;
  username: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  date_of_birth: string | null;
  gender: string | null;
  phone: string | null;
  country: string | null;
  created_at: string;
  ranking: number;
  user_status: string;
  tree_level: number | null;
  referral_code: string | null;
  sponsor_id: string | null;
}

export interface UserCommissionData {
  balance: number;
  total_commission: number;
  updated_at: string;
}

export interface UserAccount {
  id: string;
  account_number: string;
  account_share_type: string;
  account_status: string;
  balance: number;
  program_percent: number;
  broker_name: string;
  created_at: string;
  updated_at: string;
}

export interface EnhancedUserProfile {
  id: string;
  username: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  user_status: string;
  created_at: string;
  sponsor_id: string | null;
  sponsor_username: string | null;
  referral_code: string | null;
  commission_model_id: number | null;
  rank_id: number | null;
  rank_name: string | null;
  max_levels: number | null;
  partner_referral_code: string | null;
  client_referral_code: string | null;
}

export interface PersonalInfo {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  phone: string | null;
  date_of_birth: string | null;
  gender: string | null;
  country: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
}

export interface PaymentMethod {
  id: string;
  method_type: string;
  account_data: any;
  updated_at: string;
}

export interface CommissionModel {
  id: number;
  name: string;
  description: string | null;
  max_levels: number;
  level_1_percent: number;
  level_2_percent: number;
  level_3_percent: number;
  level_4_percent: number;
  level_5_percent: number;
  level_6_percent: number;
  level_7_percent: number;
  level_8_percent: number;
  level_9_percent: number;
  level_10_percent: number;
}

export class ProfileService {
  /**
   * Get user profile using the secure Supabase function
   * This prevents RLS infinite loops
   */
  static async getUserProfile(userId?: string): Promise<UserProfile | null> {
    console.log('ProfileService: Fetching user profile via RPC function');
    
    try {
      const { data, error } = await supabase.rpc('get_user_profile', {
        user_id_param: userId || null
      });

      if (error) {
        console.error('ProfileService: Error fetching user profile:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        console.log('ProfileService: No profile data found');
        return null;
      }

      console.log('ProfileService: Profile data retrieved successfully');
      return data[0] as UserProfile;
    } catch (error) {
      console.error('ProfileService: Profile fetch failed:', error);
      throw error;
    }
  }

  /**
   * Get enhanced user profile with sponsor and commission info
   */
  static async getEnhancedUserProfile(userId?: string): Promise<EnhancedUserProfile | null> {
    console.log('ProfileService: Fetching enhanced user profile via RPC function');
    
    try {
      const { data, error } = await supabase.rpc('profile_get_user_profile', {
        user_id_param: userId || null
      });

      if (error) {
        console.error('ProfileService: Error fetching enhanced user profile:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        console.log('ProfileService: No enhanced profile data found');
        return null;
      }

      console.log('ProfileService: Enhanced profile data retrieved successfully');
      return data[0] as EnhancedUserProfile;
    } catch (error) {
      console.error('ProfileService: Enhanced profile fetch failed:', error);
      throw error;
    }
  }

  /**
   * Get personal information
   */
  static async getPersonalInfo(userId?: string): Promise<PersonalInfo | null> {
    console.log('ProfileService: Fetching personal info via RPC function');
    
    try {
      const { data, error } = await supabase.rpc('profile_get_personal_info', {
        user_id_param: userId || null
      });

      if (error) {
        console.error('ProfileService: Error fetching personal info:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        console.log('ProfileService: No personal info found');
        return null;
      }

      console.log('ProfileService: Personal info retrieved successfully');
      return data[0] as PersonalInfo;
    } catch (error) {
      console.error('ProfileService: Personal info fetch failed:', error);
      throw error;
    }
  }

  /**
   * Get payment methods
   */
  static async getPaymentMethods(userId?: string): Promise<PaymentMethod[]> {
    console.log('ProfileService: Fetching payment methods via RPC function');
    
    try {
      const { data, error } = await supabase.rpc('profile_get_payment_methods', {
        user_id_param: userId || null
      });

      if (error) {
        console.error('ProfileService: Error fetching payment methods:', error);
        throw error;
      }

      console.log('ProfileService: Payment methods retrieved successfully');
      return (data || []) as PaymentMethod[];
    } catch (error) {
      console.error('ProfileService: Payment methods fetch failed:', error);
      throw error;
    }
  }

  /**
   * Get commission model
   */
  static async getCommissionModel(userId?: string): Promise<CommissionModel | null> {
    console.log('ProfileService: Fetching commission model via RPC function');
    
    try {
      const { data, error } = await supabase.rpc('profile_get_commission_model', {
        user_id_param: userId || null
      });

      if (error) {
        console.error('ProfileService: Error fetching commission model:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        console.log('ProfileService: No commission model found');
        return null;
      }

      console.log('ProfileService: Commission model retrieved successfully');
      return data[0] as CommissionModel;
    } catch (error) {
      console.error('ProfileService: Commission model fetch failed:', error);
      throw error;
    }
  }

  /**
   * Update personal information
   */
  static async updatePersonalInfo(personalInfo: Partial<PersonalInfo>): Promise<boolean> {
    console.log('ProfileService: Updating personal info via RPC function');
    
    try {
      const { data, error } = await supabase.rpc('profile_update_personal_info', {
        first_name_param: personalInfo.first_name || null,
        last_name_param: personalInfo.last_name || null,
        phone_param: personalInfo.phone || null,
        date_of_birth_param: personalInfo.date_of_birth || null,
        gender_param: personalInfo.gender || null,
        country_param: personalInfo.country || null,
        address_param: personalInfo.address || null,
        city_param: personalInfo.city || null,
        postal_code_param: personalInfo.postal_code || null
      });

      if (error) {
        console.error('ProfileService: Error updating personal info:', error);
        throw error;
      }

      console.log('ProfileService: Personal info updated successfully');
      return data as boolean;
    } catch (error) {
      console.error('ProfileService: Personal info update failed:', error);
      throw error;
    }
  }

  /**
   * Update payment method
   */
  static async updatePaymentMethod(methodType: string, accountData: any): Promise<boolean> {
    console.log('ProfileService: Updating payment method via RPC function');
    
    try {
      const { data, error } = await supabase.rpc('profile_update_payment_method', {
        method_type_param: methodType,
        account_data_param: accountData
      });

      if (error) {
        console.error('ProfileService: Error updating payment method:', error);
        throw error;
      }

      console.log('ProfileService: Payment method updated successfully');
      return data as boolean;
    } catch (error) {
      console.error('ProfileService: Payment method update failed:', error);
      throw error;
    }
  }

  /**
   * Get user accounts using the secure Supabase function
   */
  static async getUserAccounts(userId?: string): Promise<UserAccount[]> {
    console.log('ProfileService: Fetching user accounts via RPC function');
    
    try {
      const { data, error } = await supabase.rpc('get_user_accounts', {
        user_id_param: userId || null
      });

      if (error) {
        console.error('ProfileService: Error fetching user accounts:', error);
        throw error;
      }

      console.log('ProfileService: User accounts retrieved successfully');
      return (data || []) as UserAccount[];
    } catch (error) {
      console.error('ProfileService: User accounts fetch failed:', error);
      throw error;
    }
  }
}

export default ProfileService;
